/**
 * src/shell/modules/services/SovereignEventBus.ts
 *
 * Microkernel Inter-Module Reactive Event Bus (Sprint 7 / Issue 7.2).
 * Provides domain-namespaced pub/sub coordination between widgets across slots,
 * with token-bucket rate limiting, unmount subscription cleanup, and tamper-evident audit hooks.
 */

export interface BusEventRecord {
  id: string;
  moduleId: string;
  topic: string;
  timestamp: number;
  payloadSize: number;
}

interface TokenBucket {
  tokens: number;
  lastRefill: number;
}

type EventHandler<T = any> = (payload: T) => void;

interface ListenerEntry {
  moduleId: string;
  topicPattern: string;
  handler: EventHandler;
  regex: RegExp;
}

export class SovereignEventBus {
  private static instance: SovereignEventBus;
  private listeners: Set<ListenerEntry> = new Set();
  private rateLimits: Map<string, TokenBucket> = new Map();
  private recentEvents: BusEventRecord[] = [];
  private readonly MAX_BURST = 50; // max burst tokens
  private readonly REFILL_RATE = 10; // tokens per second
  private readonly MAX_HISTORY = 100;

  private constructor() {}

  public static getInstance(): SovereignEventBus {
    if (!SovereignEventBus.instance) {
      SovereignEventBus.instance = new SovereignEventBus();
    }
    return SovereignEventBus.instance;
  }

  /**
   * Helper to compile topic patterns with wildcard support (* matches any token)
   */
  private compilePattern(pattern: string): RegExp {
    const escaped = pattern
      .split('*')
      .map((s) => s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'))
      .join('.*');
    return new RegExp(`^${escaped}$`);
  }

  /**
   * Token bucket rate limiter check
   */
  private checkRateLimit(moduleId: string): boolean {
    const now = Date.now();
    let bucket = this.rateLimits.get(moduleId);
    if (!bucket) {
      bucket = { tokens: this.MAX_BURST, lastRefill: now };
      this.rateLimits.set(moduleId, bucket);
    } else {
      const elapsedSec = (now - bucket.lastRefill) / 1000;
      bucket.tokens = Math.min(this.MAX_BURST, bucket.tokens + elapsedSec * this.REFILL_RATE);
      bucket.lastRefill = now;
    }

    if (bucket.tokens >= 1) {
      bucket.tokens -= 1;
      return true;
    }
    return false;
  }

  /**
   * Publish an event to the bus.
   */
  public publish(moduleId: string, topic: string, payload: unknown): { success: boolean; error?: string } {
    if (!topic || typeof topic !== 'string' || !topic.includes(':')) {
      return { success: false, error: 'INVALID_TOPIC: Topics must be namespaced (e.g. domain:action)' };
    }

    // Rate limit check
    if (!this.checkRateLimit(moduleId)) {
      console.warn(`[SovereignEventBus] Rate limit exceeded for module "${moduleId}" on topic "${topic}"`);
      return { success: false, error: 'RATE_LIMIT_EXCEEDED: Token bucket exhausted' };
    }

    let payloadSize = 0;
    try {
      payloadSize = JSON.stringify(payload)?.length || 0;
    } catch {}

    const record: BusEventRecord = {
      id: `evt_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      moduleId,
      topic,
      timestamp: Date.now(),
      payloadSize,
    };

    this.recentEvents.unshift(record);
    if (this.recentEvents.length > this.MAX_HISTORY) {
      this.recentEvents.pop();
    }

    // Dispatch to matching listeners
    let deliveredCount = 0;
    for (const entry of this.listeners) {
      if (entry.regex.test(topic)) {
        try {
          entry.handler(payload);
          deliveredCount++;
        } catch (err: any) {
          console.error(`[SovereignEventBus] Handler crash in listener module "${entry.moduleId}" on topic "${topic}":`, err);
        }
      }
    }

    return { success: true };
  }

  /**
   * Subscribe to a topic or topic wildcard pattern.
   */
  public subscribe<T = unknown>(
    moduleId: string,
    topicPattern: string,
    handler: EventHandler<T>
  ): { unsubscribe(): void } {
    const entry: ListenerEntry = {
      moduleId,
      topicPattern,
      handler,
      regex: this.compilePattern(topicPattern),
    };

    this.listeners.add(entry);

    return {
      unsubscribe: () => {
        this.listeners.delete(entry);
      },
    };
  }

  /**
   * Clear all subscriptions belonging to a specific module (invoked on module unmount).
   */
  public clearModuleListeners(moduleId: string): void {
    const toRemove: ListenerEntry[] = [];
    for (const entry of this.listeners) {
      if (entry.moduleId === moduleId) {
        toRemove.push(entry);
      }
    }
    toRemove.forEach((e) => this.listeners.delete(e));
  }

  /**
   * Telemetry inspection for admin diagnostics.
   */
  public getRecentEvents(): readonly BusEventRecord[] {
    return [...this.recentEvents];
  }

  public reset(): void {
    this.listeners.clear();
    this.rateLimits.clear();
    this.recentEvents = [];
  }
}

export const sovereignEventBus = SovereignEventBus.getInstance();
